import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, Button } from '@lb-classroom/ui';
import { useRouter } from 'next/router';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { withTranslation, i18n } from 'next-i18next';
import { TFunction } from 'i18next';
import { hasRole } from '@lb-classroom/feature-auth';
import TextInput from '../../UI/atoms/form/TextInput';
import * as AuthActions from '../../../actions/auth';
import * as ModalActions from '../../../actions/modal';
import * as FlashActions from '../../../actions/flash';
import UserValidator from '../../../validators/UserValidator';

interface User {
  profile_type: string;
  locale?: {
    i18n_form: string;
  };
}

interface RootState {
  auth: {
    user: User | null;
    waiting: boolean;
    resetPasswordLinkSent: boolean;
  };
}

interface StateProps {
  user: User | null;
  waiting: boolean;
  resetPasswordLinkSent: boolean;
}

interface DispatchProps {
  authActions: typeof AuthActions;
  flashActions: typeof FlashActions;
  modalActions: typeof ModalActions;
}

interface OwnProps {
  t: TFunction;
  inModal?: boolean;
  clearModalMessage?: () => void;
}

type ForgotPasswordProps = StateProps & DispatchProps & OwnProps;

function mapStateToProps(state: RootState): StateProps {
  const { auth } = state;
  const { user, waiting, resetPasswordLinkSent } = auth;
  return {
    user,
    waiting,
    resetPasswordLinkSent,
  };
}

function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
  return {
    authActions: bindActionCreators<any, any>(AuthActions, dispatch),
    flashActions: bindActionCreators<any, any>(FlashActions, dispatch),
    modalActions: bindActionCreators<any, any>(ModalActions, dispatch),
  };
}

interface ValidationResult {
  success: boolean;
  message?: string;
}

interface FormState {
  email: string;
  emailValidationResult: ValidationResult | null;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = (props) => {
  const {
    t,
    user,
    waiting,
    flashActions,
    authActions,
    resetPasswordLinkSent,
    inModal = false,
  } = props;
  const router = useRouter();
  const [state, setState] = useState<FormState>({
    email: '',
    emailValidationResult: null,
  });
  // Track if redirect has happened to prevent duplicate redirects
  const hasRedirected = useRef(false);

  useEffect(() => {
    return () => {
      flashActions.clearMessage();
    };
  }, [flashActions]);

  useEffect(() => {
    if (hasRedirected.current) return;

    if (!waiting && user) {
      hasRedirected.current = true;
      const isTutor = hasRole(user.profile_type, 'Tutor');
      const isStudent = hasRole(user.profile_type, 'Student');

      // Tutors (including admins with both roles) go to tutor dashboard
      if (isTutor) {
        router.push('/t/dashboard');
      } else if (isStudent) {
        // Students go to student dashboard with locale
        if (user.locale?.i18n_form) {
          i18n.changeLanguage(user.locale.i18n_form);
          router.push('/s/dashboard', '/s/dashboard', {
            locale: user.locale.i18n_form,
          });
        } else {
          router.push('/s/dashboard');
        }
      } else {
        // Unknown role, redirect to home
        router.push('/');
      }
    }
  }, [user, waiting, router]);

  const onUpdateEmail = (value: string) => {
    setState({
      ...state,
      email: value,
    });
  };

  const isValid = (results: ValidationResult[]) => {
    // Always update state using functional updater to avoid stale state issues
    setState((prev) => ({
      ...prev,
      emailValidationResult: results[0],
    }));
    return results[0].success;
  };

  const onSubmit = async (event: React.FormEvent | React.KeyboardEvent) => {
    event.preventDefault();
    flashActions.clearMessage();
    const { email } = state;
    const emailValidationResult = await UserValidator.emailValidator({
      name: t('form.email'),
      value: email,
      required: true,
      t,
    });
    if (isValid([emailValidationResult])) {
      authActions.sendResetPasswordLink(email);
    }
  };

  const emailProps = {
    placeholder: t('form.email'),
    className: 'email',
    value: state.email,
    name: 'email',
    type: 'email',
    updateHandler: onUpdateEmail,
    validationResult: state.emailValidationResult,
    invalidLabel: true,
    onKeyDown: onSubmit,
  };

  return (
    <Card className={inModal ? 'border-0' : ''}>
      <CardContent className={inModal ? 'm-0 p-0' : ''}>
        <p>{t('modals.forgotPassword.explanation')}</p>
        <form method="post" className="login-form" onSubmit={onSubmit}>
          <div className="control-group mb-4">
            <TextInput {...emailProps} />
          </div>
          <div className="control-group mb-4">
            <Button
              onClick={onSubmit}
              className="w-full"
              size="lg"
              type="submit"
              disabled={waiting || resetPasswordLinkSent}
            >
              {t('modals.forgotPassword.submit')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

const wrapped = withTranslation('common')(ForgotPassword);

export default connect(mapStateToProps, mapDispatchToProps)(wrapped);
